import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { RotateCcw, Send, Eye, Save } from "lucide-react";

export default function EmailTemplatesAdmin() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [editing, setEditing] = useState(null); // {key, subject, body_html, variables, sample, name}
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/admin/email-templates");
      setTemplates(data);
      if (!selectedKey && data.length) setSelectedKey(data[0].key);
    } catch {
      toast.error("Failed to load templates");
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedKey) return;
    const t = templates.find((x) => x.key === selectedKey);
    if (t) setEditing({ ...t });
  }, [selectedKey, templates]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await http.put(`/admin/email-templates/${editing.key}`, {
        subject: editing.subject,
        body_html: editing.body_html,
      });
      toast.success("Template saved");
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!editing) return;
    if (!window.confirm("Reset this template to the default? Your edits will be lost.")) return;
    try {
      await http.post(`/admin/email-templates/${editing.key}/reset`);
      toast.success("Restored default");
      await load();
    } catch {
      toast.error("Reset failed");
    }
  };

  const testSend = async () => {
    if (!testEmail || !editing) { toast.error("Enter a test email"); return; }
    try {
      await http.post(`/admin/email-templates/${editing.key}/test-send`, { email: testEmail.trim() });
      toast.success(`Test sent to ${testEmail}`);
    } catch {
      toast.error("Test send failed");
    }
  };

  const insertVariable = (varName) => {
    if (!editing) return;
    // Append to body_html — admin can drag/cut/paste from there
    setEditing({ ...editing, body_html: (editing.body_html || "") + ` {{${varName}}}` });
    toast.success(`Inserted {{${varName}}} at end of body`);
  };

  const renderedPreview = (() => {
    if (!editing) return { subject: "", body_html: "" };
    const ctx = editing.sample || {};
    const replace = (s) => (s || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => (ctx[k] !== undefined && ctx[k] !== null ? String(ctx[k]) : ""));
    return { subject: replace(editing.subject), body_html: replace(editing.body_html) };
  })();

  if (!editing) return <div className="text-[#8a857d] font-body text-sm">Loading templates…</div>;

  return (
    <div className="grid lg:grid-cols-[280px,1fr] gap-6" data-testid="email-templates-admin">
      {/* Sidebar list */}
      <div className="border border-white/10 bg-[#0a0a0a]">
        <div className="px-4 py-3 border-b border-white/10 font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Templates</div>
        <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
          {templates.map((t) => (
            <button
              key={t.key}
              onClick={() => setSelectedKey(t.key)}
              data-testid={`tpl-row-${t.key}`}
              className={`w-full text-left px-4 py-3 hover:bg-white/5 transition-colors ${selectedKey === t.key ? "bg-white/5 border-l-2 border-[#C9A055]" : ""}`}
            >
              <div className="font-ui text-[11px] tracking-[0.18em] text-[#F4F1EA] uppercase">{t.name}</div>
              <div className="font-body text-[11px] text-[#8a857d] mt-1 truncate">{t.subject}</div>
              {t.is_override && <div className="mt-1 inline-block font-ui text-[9px] tracking-[0.22em] text-[#C9A055]">CUSTOM</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="space-y-4">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <div className="font-display text-2xl text-[#F4F1EA]">{editing.name}</div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mt-1">KEY: {editing.key}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowPreview(true)} data-testid="tpl-preview-btn" className="px-4 py-2 border border-white/20 text-[#F4F1EA] hover:border-[#C9A055] font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
              <Eye size={12}/> Preview
            </button>
            <button onClick={reset} data-testid="tpl-reset-btn" className="px-4 py-2 border border-white/20 text-[#9a9690] hover:text-[#d97a7a] hover:border-[#d97a7a] font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
              <RotateCcw size={12}/> Reset
            </button>
            <button onClick={save} disabled={saving} data-testid="tpl-save-btn" className="px-4 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2 disabled:opacity-40">
              <Save size={12}/> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* Subject */}
        <div className="border border-white/10 bg-[#0a0a0a] p-4">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Subject</div>
          <input
            type="text"
            value={editing.subject || ""}
            onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
            className="w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-ui text-[14px] focus:border-[#C9A055] focus:outline-none"
            data-testid="tpl-subject-input"
          />
        </div>

        {/* Variable picker */}
        {(editing.variables || []).length > 0 && (
          <div className="border border-white/10 bg-[#0a0a0a] p-4">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Available Variables (click to append)</div>
            <div className="flex flex-wrap gap-2">
              {editing.variables.map((v) => (
                <button
                  key={v.name}
                  onClick={() => insertVariable(v.name)}
                  data-testid={`tpl-var-${v.name}`}
                  title={v.desc}
                  className="px-2 py-1 border border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.12em] transition-colors"
                >
                  {`{{${v.name}}}`}
                </button>
              ))}
            </div>
            <p className="font-body text-[11px] text-[#8a857d] mt-3">Hover a variable for its description. Variables are replaced when the email is sent; missing values render as empty.</p>
          </div>
        )}

        {/* Body */}
        <div className="border border-white/10 bg-[#0a0a0a] p-4">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Body (HTML)</div>
          <RichTextEditor
            value={editing.body_html || ""}
            onChange={(html) => setEditing({ ...editing, body_html: html })}
          />
        </div>

        {/* Test send */}
        <div className="border border-white/10 bg-[#0a0a0a] p-4">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Send a Test (uses sample data)</div>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 min-w-[240px] bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-ui text-[13px]"
              data-testid="tpl-test-email-input"
            />
            <button
              onClick={testSend}
              data-testid="tpl-test-send-btn"
              className="px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2"
            >
              <Send size={12}/> Send Test
            </button>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setShowPreview(false)}>
          <div className="bg-[#050505] border border-[#C9A055]/30 max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">PREVIEW (Sample data)</div>
                <div className="font-display text-lg text-[#F4F1EA] mt-1">Subject: {renderedPreview.subject}</div>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-[#8a857d] hover:text-[#F4F1EA] text-2xl">×</button>
            </div>
            <div className="p-6 bg-[#050505]">
              <iframe
                title="email-preview"
                srcDoc={`<div style="background:#0a0a0a;color:#F4F1EA;font-family:Arial;padding:32px;border:1px solid rgba(201,160,85,0.2);">${renderedPreview.body_html}</div>`}
                className="w-full min-h-[480px] bg-[#0a0a0a] border-0"
                data-testid="tpl-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
